// Phase 6.7 C — «query похожа на IGS station code» heuristic.
//
// The feed search box returns a mix of job-cards + batch-summary
// cards. When the operator types a 4-character station code (e.g.
// `FRDN`, `AMC4`), the top of the result list ends up being a
// batch-card whose tag contains a job at that station — NOT the
// jobs themselves. That's the wrong UX: the operator wanted to see
// «what's the state of FRDN» but landed on «what's the state of
// Random25».
//
// The heuristic below is intentionally narrow: an IGS station code
// is exactly 4 characters of `[A-Z0-9]` with at least one letter
// (e.g. `FRDN`, `AMC4`, `BOR1`). Pure digits (`1234`) don't trip
// it because operators occasionally hunt for job IDs that happen
// to be four digits. Multi-character search terms above or below 4
// chars are not station codes by IGS convention.
//
// Spec §C.6 acknowledges the edge case: a 4-character tag like
// `prod` or `test` would also trip this heuristic, but real
// 4-character batch tags are vanishingly rare in this project
// (every tag we've seen has a date suffix or the word `Backfill_`).
// If a future operator hits this, we'll add a toggle.

/** Returns `true` when the trimmed query looks like an IGS 4-character
 *  station code: 4 of `[A-Z0-9]` with at least one letter. The
 *  comparison is case-insensitive — `frdn`, `FRDN`, and `FrDn`
 *  all match.
 *
 *  Returns `false` for every other shape: longer queries, shorter
 *  queries, pure-digit 4-character queries, or queries containing
 *  punctuation.
 */
export function isLikelyStationCode(query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length !== 4) return false;
  const upper = trimmed.toUpperCase();
  // 4 chars of A-Z / 0-9 only — punctuation, space, Cyrillic etc. fail.
  if (!/^[A-Z0-9]{4}$/.test(upper)) return false;
  // At least one letter — `1234` is a job id, not a station code.
  if (!/[A-Z]/.test(upper)) return false;
  return true;
}
